import React, {useContext, useEffect, useRef, useState} from 'react';
import {useInvoiceRegistryCheck} from '../components/useInvoiceRegistryCheck';
import {Button, Form, Modal} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {Invoice, InvoiceProtocolPick, MergeableSibling} from '../models/table-models';
import {transformInvoiceToInput} from '../utils/helper-functions';
import {Fields} from '../utils/Enums/InvoiceFields';
import {Turnover} from '../models/user-models';
import dateFormat from 'dateformat';
import {dateTime} from '../utils/Enums/Common';
import {resourceCache, useAsyncResource} from 'use-async-resource';
import {useGetUsers} from '../utils/users-hooks';
import {
    addNumberToInvoice,
    checkMergeableSiblings,
    saveProtocolPicks,
    confirmSaleInvoice,
    getCreditNotePDF,
    getInvoicePDF,
    getInvoicePreview,
    mergeAndConfirmInvoices,
    useGetInvoices
} from "../utils/clients-hooks";
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import {AuthContext} from "../context/AuthContext";
import {DocumentView, InvoicePreview} from "../models/dashboard-models";
import EditableInvoice from "../components/invoiceHtml/EditableInvoice";
import {EditDocument} from "../components/AddInvoicesToDocument";
import {MergeInvoicesDialog} from "../components/MergeInvoicesDialog";
import {showBackendError} from "../utils/backend-error";

interface InvoiceDetailsProps {
    item?: Invoice;
    turnover?: Turnover;
    setSelectedItem: (item?: Invoice) => void;
    title: string;
    refetchEntitiesHook?: () => void;
    /**
     * Изрично указание, че диалогът е отворен от ред на кредитно известие. Нужно е там, където редът и
     * фактурата му сочат един и същи обект - във "Всички документи" двата реда носят едно и също
     * invoicings[0], така че по него не се различават.
     */
    isCreditNote?: boolean;
}

export const InvoiceView = ({item, setSelectedItem, title, turnover, refetchEntitiesHook, isCreditNote}: InvoiceDetailsProps) => {
    const {t} = useTranslation('translation', {keyPrefix: 'common'});
    const {loggedUser} = useContext(AuthContext);
    const [invoicePreviewData, setInvoicePreviewData] = useState<InvoicePreview>({} as InvoicePreview);
    const editableInvoiceRef = useRef<any>();
    const [hasChanges, setHasChanges] = useState(false);
    const [load, setLoad] = useState(false);
    const [showChange, setShowChange] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [mergeableSiblings, setMergeableSiblings] = useState<MergeableSibling[]>([]);

    // Редът на кредитното известие се познава по documentType: производният ред идва с 'creditnote' за
    // фактура и 'storno' за стокова разписка. Дотук изборът висеше само на отрицателен оборот, а turnover
    // се подава единствено от Оборот и Всички документи - в продажбите и задълженията го няма, така че
    // натискането на реда на известието отваряше прегледа на фактурата.
    const isCreditNoteRow = isCreditNote || item?.documentType === 'creditnote' || item?.documentType === 'storno';

    useEffect(() => {
        if (!!item) {
            getInvoicePreview(
                item?.document?.id,
                item?.document?.creditNoteNumber != null
                    ? isCreditNoteRow || !!(turnover?.cash && turnover?.cash < 0)
                    : false,
            ).then((value) => {
                setInvoicePreviewData(()=>value);
                setLoad(true)
            });
        }
    }, [item]);

    const print = async () => {
        const pdfFile = await getInvoicePDF(item?.document?.id, loggedUser?.id);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText/>, toastOptionsTopRight);
            return false;
        }
    };

    const handleCreditNoteSubmit = async () => {
        setIsLoading(true);
        const pdfFile = await getCreditNotePDF(null, item?.document?.id, loggedUser?.id);
        resourceCache(useGetInvoices).clear();
        resourceCache(getInvoicePreview).clear();
        refetchEntitiesHook && refetchEntitiesHook();
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            setIsLoading(false);
            return true;
        } else {
            setIsLoading(false);
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText/>, toastOptionsTopRight);
            return false;
        }
    };

    const { checkBeforeIssue, registryDialog, registryBusy } = useInvoiceRegistryCheck();
    const actionsLocked = registryBusy || showMergeDialog;

    const addNumber = async (fields: Invoice | undefined) => {
        if (!fields?.id) return;
        await checkBeforeIssue(fields, () => continueNumbering(fields));
    };

    const continueNumbering = async (fields: Invoice) => {
        if (!fields?.id) return;

        // Продажбата държи документа си едно към едно: няма сестрински фактури, които да се слеят с нея,
        // а и mergeableSiblings търси id-то в таблицата на наемите - за продажба то не се намира там и
        // потвърждението гърмеше с "Invoicing not found". Номерирането ѝ е отделен път.
        if (fields.isSaleInvoice) {
            setIsLoading(true);
            try {
                await confirmSaleInvoice(fields.id);
                resourceCache(getInvoicePreview).clear();
                refetchEntitiesHook && refetchEntitiesHook();
                toast.success(<CustomToastContent content={[t('invoiceComfirm')]}/>, toastOptionsTopRight);
            } catch (error) {
                showBackendError(error);
            } finally {
                setIsLoading(false);
            }
            return;
        }

        const siblings = await checkMergeableSiblings(fields.id);

        if (fields.documentType === 'invoice' || (siblings && siblings.length > 0)) {
            setMergeableSiblings(siblings || []);
            setShowMergeDialog(true);
        } else {
            await confirmSingleInvoice(fields.id);
        }
    };

    const confirmSingleInvoice = async (invoiceId: number, corrections?: import('../models/invoice-corrections').InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        setIsLoading(true);

        if (corrections !== undefined || breakdowns?.length) {
            await mergeAndConfirmInvoices([invoiceId], corrections, breakdowns);
        } else {
            await addNumberToInvoice(invoiceId);
        }

        resourceCache(getInvoicePreview).clear();
        refetchEntitiesHook && refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('invoiceComfirm')]}/>, toastOptionsTopRight);
        setShowMergeDialog(false);
        setIsLoading(false);
    };


    const confirmOnlyThis = async (picks: InvoiceProtocolPick[], corrections?: import('../models/invoice-corrections').InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        if (picks.length > 0) {
            await saveProtocolPicks(picks);
        }

        try {
            item?.id && await confirmSingleInvoice(item.id, corrections, breakdowns);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMergeSelected = async (invoicingIds: number[], picks: InvoiceProtocolPick[], corrections?: import('../models/invoice-corrections').InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        setIsLoading(true);
        try {
            // Запазваме избора преди издаването, за да няма издадена фактура при неуспешен запис на протоколите.
            if (picks.length > 0) {
                await saveProtocolPicks(picks);
            }

            await mergeAndConfirmInvoices(invoicingIds, corrections, breakdowns);
            setShowMergeDialog(false);

            resourceCache(getInvoicePreview).clear();
            refetchEntitiesHook && refetchEntitiesHook();
            toast.success(<CustomToastContent content={[t('invoiceComfirm')]}/>, toastOptionsTopRight);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
        <Modal show={!!item} onHide={() => {
            if (actionsLocked) return;
            setSelectedItem(undefined);
            setLoad(false);
            setShowChange(false);
        }} size='lg' centered keyboard={!actionsLocked} backdrop={actionsLocked ? 'static' : true}>
            <Modal.Header closeButton={!actionsLocked}>
                <Modal.Title id='contained-modal-title-vcenter'>{title + " "}
                    <label className={"text-danger"}>
                        {(item?.document?.invoiceNumber ? "№" + (turnover && turnover.cash < 0 && item.document?.creditNoteNumber != undefined ? item.document?.creditNoteNumber : item.document?.invoiceNumber) : item?.documentType == "invoice" ? "(" + t("Missing invoice number") + ")" : "(" + t("Unpaid debt") + ")")}
                    </label>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {registryDialog}
                {
                    ((isLoading && !registryBusy) || !load) ?
                    <div className={"d-flex justify-content-center"}>
                        <div className={"spinner-border"} role={"status"}>
                            <span className={"visually-hidden"}>Loading...</span>
                        </div>
                    </div>
                        :
                        showChange ?
                            <EditDocument itemToEdit={item}
                                          setIsLoading={setIsLoading}
                                          setShowModal={() => {
                                              setShowChange(() => false)
                                          }}
                                          // Покрие ли последното плащане остатъка, фактурата е приключена
                                          // и диалогът няма какво повече да покаже.
                                          onInvoiceClosed={() => {
                                              setShowChange(false);
                                              setSelectedItem(undefined);
                                          }}
                                          invoices={item ? [item] : []}
                                          refetchEntitiesHook={refetchEntitiesHook || (() => {
                                          })}/>
                            :
                            <EditableInvoice
                                {...{invoicePreviewData}}
                                setHasChanges={setHasChanges}
                                ref={editableInvoiceRef}
                            />
                }
                {/*{!!item && <InvoiceViewInner item={item} />}*/}
                {/*{!!turnover && <TurnoverViewInner turnover={turnover} />}*/}
            </Modal.Body>
            {showChange ?
                <Modal.Footer>
                    <Button disabled={actionsLocked} onClick={() => setShowChange(false)} size='sm' className='info-modal-action'>
                        <span className='info-modal-action-label'>{t('close')}</span>
                    </Button>
                    <Button disabled={actionsLocked} form='form' type='submit' size='sm' className='btn-success info-modal-action'>
                        <span className='info-modal-action-label'>{t('save')}</span>
                    </Button>
                </Modal.Footer>
                :
                <Modal.Footer>
                    {!item?.documentOnly && <Button disabled={actionsLocked} onClick={() => setShowChange(true)}>{t('edit')}</Button>}
                    {/* getCreditNotePDF номерира известието като страничен ефект от печата - това е
                        целият сторно път и за продажбите. Стоката не се връща в склада и оборотът не се
                        сваля; кредитното известие е документът, не движение по наличността. */}
                    {!item?.documentOnly && item?.document?.invoiceNumber && item?.document?.paymentType?.toLowerCase() === 'bank' && !item?.document?.creditNoteNumber &&
                        <Button disabled={actionsLocked} form='form' type='submit'
                                onClick={() => handleCreditNoteSubmit()}>{t('printCreditNote')}</Button>}
                    {item?.document?.invoiceNumber && item?.document?.creditNoteNumber &&
                        <Button disabled={actionsLocked} form='form' type='submit'
                                onClick={() => handleCreditNoteSubmit()}>{t('viewCreditNote')}</Button>}
                    {item?.document?.invoiceNumber == undefined && item?.paymentMethod == "bank" &&
                        <Button disabled={actionsLocked} className={"btn-success"} onClick={() => addNumber(item)}>{t('confirm')}</Button>}
                    <Button disabled={actionsLocked}
                        onClick={() => item?.documentType === "creditnote" ? handleCreditNoteSubmit() : print()}>{t('print')}</Button>
                    <Button disabled={actionsLocked} onClick={() => setSelectedItem(undefined)}>{t('close')}</Button>
                </Modal.Footer>
            }
        </Modal>

        <MergeInvoicesDialog
            show={showMergeDialog}
            onHide={() => setShowMergeDialog(false)}
            currentInvoice={item}
            siblings={mergeableSiblings}
            onConfirmOnlyThis={confirmOnlyThis}
            onMergeSelected={handleMergeSelected}
        />
        </>
    );
};

const InvoiceViewInner = ({item}: { item: Invoice }) => {
    return (
        <div className='mb-2 w-100'>
            <div className='w-100 d-flex flex-wrap mb-3 justify-content-between'>
                <div className='flex-column w-50 pe-lg-5'>
                    {Fields.left.map((value, index) => {
                        const unit = value === 'fee' || value === 'total' ? item.currency : value === 'discount' ? '%' : '';
                        return (
                            <InvoiceViewField
                                key={`${index}-left`}
                                fieldName={value as keyof Invoice}
                                unit={unit}
                                {...{index, item}}
                            />
                        );
                    })}
                </div>
                <div className='flex-column w-50 pe-lg-5'>
                    {Fields.right.map((value, index) => (
                        <InvoiceViewField
                            key={`${index}-right`}
                            fieldName={value as keyof Invoice}
                            {...{index, item}}
                        />
                    ))}
                </div>
            </div>
            <div className='w-100 d-flex mb-3 flex-wrap justify-content-between'>
                <InvoiceViewField fieldName='comment' item={item} index={1}/>
            </div>
        </div>
    );
};

export const InvoiceViewField = ({
                                     fieldName,
                                     index,
                                     item,
                                     unit,
                                 }: {
    fieldName: keyof Invoice;
    index: number;
    item: Invoice;
    unit?: string;
}) => {
    // Показваните полета са затворен списък (EditFields/Fields) и никое от тях не е масив, а
    // item[fieldName] връща съюза на всички стойности на Invoice - включително documentInvoiceIds.
    let value = item[fieldName] as string | number | boolean | Date | DocumentView | undefined;
    if (fieldName === 'paid') value = item.paymentMethod;
    if (fieldName === 'invoiceNumber') value = item.document?.invoiceNumber;
    if (fieldName === 'countMonths') value = Math.trunc(value as number);
    value = transformInvoiceToInput(fieldName as string, value);
    return <DetailField {...{fieldName, value, index, unit}} />;
};

const DetailField = ({
                         fieldName,
                         value,
                         index,
                         unit,
                     }: {
    fieldName: string;
    value: string | number | undefined | Date | DocumentView | boolean;
    index: number;
    unit?: string;
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'invoice'});
    return (
        <div key={index} className='d-flex flex-wrap mb-3 justify-content-between'>
            <div className='d-flex flex-column '>{t(fieldName)}:</div>
            <div className='ms-4 d-flex flex-column text-break'>
                {value && (fieldName === 'paid' || fieldName === 'state')
                    ? t(String(value).toLowerCase())
                    : value
                        ? value + ` ${unit ?? ''}`
                        : value === '0'
                            ? '0'
                            : '-'}
            </div>
        </div>
    );
};

const TurnoverViewInner = ({turnover}: { turnover: Turnover }) => {
    const {t} = useTranslation('translation', {keyPrefix: 'common'});
    const [getAllUsers] = useAsyncResource(useGetUsers, {});
    const {items: users} = getAllUsers();

    return (
        <div>
            <DetailField
                fieldName={'collectedBy'}
                value={String(users.find((user) => user.id === turnover.userId)?.name)}
                index={10}
            />
            <DetailField fieldName={'taskType'} value={t(turnover.taskType) as string} index={11}/>
            <DetailField fieldName={'paidAt'} value={dateFormat(turnover.createdAt, dateTime)} index={12}/>
        </div>
    );
};
